import { Div, P } from '@kizi/core-components';
import { IconV2 } from '@kizi/shared-components';
import React from 'react';
import { FormPropsWithValue } from '../HOCs';
import { TileRadioButton } from './TileRadioButton';
import { TileRadioLabel } from './TileRadioLabel';
import {
  ComponentDescriptorOrArray,
  getRendererFromDescriptor,
} from '@kizi/comparator-core';
import { TileRadioLabelWithChildren } from './TileRadioLabelWithChildren';

export interface TileRadioGroupItem {
  text: string;
  subText?: string;
  icon?: string;
  value: string | number;
  children: ComponentDescriptorOrArray;
  hasChildren?: boolean;
}

export interface TileRadioGroupProps extends FormPropsWithValue {
  radios: TileRadioGroupItem[];
  direction: 'horizontal' | 'vertical';
  heading?: ComponentDescriptorOrArray;
  buttonMaxWidth?: string;
  displayTilesInRow?: boolean;
  style?: any;
  onClick?: React.MouseEventHandler;
  children?: any;
  tileWithChildren?: boolean;
  customStyles?: any;
}

export const TileRadioGroupOG: React.VFC<TileRadioGroupProps> = ({
  radios,
  direction = 'vertical',
  heading,
  buttonMaxWidth,
  displayTilesInRow = true,
  style,
  onClick,
  children,
  tileWithChildren,
  customStyles,
  ...input
}) => {
  console.log(direction);
  const isVertical = direction === 'vertical' || displayTilesInRow;

  const onChange = React.useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >(
    (event) => {
      input.onChange?.({
        ...event,
        target: {
          name: event.target.name,
          value: event.target.value,
          checked: event.target.checked,
        },
      });
    },
    [input.value, input.onChange, input.name],
  );
  const Component = tileWithChildren
    ? TileRadioLabelWithChildren
    : TileRadioLabel;
  const items = radios?.map(
    ({ text, subText, icon, value, children, hasChildren }) => {
      return {
        label: (
          <Component
            text={text}
            subText={subText}
            icon={icon}
            direction={direction}
            children={children}
          />
        ),
        radio: { value: value },
        hasChildren,
      };
    },
  );

  return (
    <Div display="flex" flexDirection="column" gridGap={2}>
      {heading ? <>{getRendererFromDescriptor(heading)}</> : null}
      <Div display="flex" flexDirection="column" gridGap={[3, null, 4]}>
        {input._error && (
          <Div
            textAlign={['left', null, 'center']}
            display="flex"
            gridGap={2}
            alignItems="center"
            justifyContent={['flex-start', null, 'center']}
          >
            <IconV2 name="alert" width={20} height={20} fill="error" />
            <P
              id="mainCoverageSectionErrorMessage"
              variant="footnoteError"
              data-testid="MainCoverageSection.error.text"
            >
              {input._error}
            </P>
          </Div>
        )}
        <Div
          display={displayTilesInRow ? 'flex' : 'grid'}
          flexDirection={isVertical ? 'row' : 'column'}
          gridGap={[3, 3, 3]}
          css={
            isVertical
              ? {
                  gridTemplateColumns: [
                    null,
                    null,
                    `repeat(${items?.length || 0}, 1fr)`,
                  ],
                }
              : {
                  gridTemplateRows: [
                    null,
                    null,
                    `repeat(${items?.length || 0}, 1fr)`,
                  ],
                }
          }
          width={'fit-content'}
        >
          {items?.map((item, idx) => {
            return (
              <TileRadioButton
                key={idx}
                style={style}
                direction={direction}
                buttonMaxWidth={buttonMaxWidth}
                customStyles={customStyles}
                {...item}
                radio={
                  item.radio
                    ? {
                        ...item.radio,
                        name: input.name,
                        checked:
                          String(item.radio.value) === String(input.value),
                        onChange,
                        onClick,
                      }
                    : undefined
                }
              />
            );
          })}
        </Div>
        {items?.map((item) => {
          return (
            item?.hasChildren &&
            String(item.radio.value) === String(input.value) && (
              <Div mb={3}>{children}</Div>
            )
          );
        })}
      </Div>
    </Div>
  );
};
